import React from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Button from "../../ui/Button";
import { colors, spacing, typography } from "../../ui/tokens";
import {
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  surfaceCardBodyStyle,
  surfaceCardStyle,
  toolbarStyle,
} from "../../ui/pagePatterns";
import AuditExecutionDetail from "./AuditExecutionDetail";
import AuditList from "./AuditList";
import AuditPlans from "./AuditPlans";
import AuditUniverseList from "./AuditUniverseList";

const shellStyle = {
  ...pageShellStyle,
  gap: spacing.xl,
};

const headerStyle = {
  ...pageHeaderStyle,
};

const tabRowStyle = {
  ...toolbarStyle,
};

const panelStyle = {
  display: "grid",
  gap: spacing.lg,
};

const subnavStyle = {
  ...toolbarStyle,
};

const moduleNavCardStyle = {
  ...surfaceCardStyle,
};

const moduleNavBodyStyle = {
  ...surfaceCardBodyStyle,
  display: "grid",
  gap: spacing.md,
};

const helperTextStyle = {
  margin: 0,
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

export default function AuditModule() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: auditId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = location.pathname.startsWith("/audit/planning") ? "planning" : "audits";
  const planningView = searchParams.get("view") === "entities" ? "entities" : "plans";

  const updatePlanningView = (view) => {
    const next = new URLSearchParams(searchParams);
    if (view === "entities") {
      next.set("view", "entities");
    } else {
      next.delete("view");
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <div style={shellStyle}>
      <div style={headerStyle}>
        <div style={pageHeaderTitleBlockStyle}>
          <h1 style={pageTitleStyle}>Audits</h1>
          <p style={pageSubtitleStyle}>
            Manage audit execution, maintain the planning cycle, and coordinate auditable entities from one workspace.
          </p>
        </div>
      </div>

      <div style={moduleNavCardStyle}>
        <div style={moduleNavBodyStyle}>
          <div role="tablist" aria-label="Audit module tabs" style={tabRowStyle}>
            <Button
              type="button"
              variant={activeTab === "audits" ? "primary" : "secondary"}
              aria-selected={activeTab === "audits"}
              onClick={() => navigate("/audit/audits")}
            >
              Audits
            </Button>
            <Button
              type="button"
              variant={activeTab === "planning" ? "primary" : "secondary"}
              aria-selected={activeTab === "planning"}
              onClick={() => navigate("/audit/planning")}
            >
              Audit Planning
            </Button>
          </div>
          {activeTab === "planning" ? (
            <>
              <div style={subnavStyle}>
                <Button
                  type="button"
                  variant={planningView === "plans" ? "primary" : "secondary"}
                  onClick={() => updatePlanningView("plans")}
                >
                  Plans
                </Button>
                <Button
                  type="button"
                  variant={planningView === "entities" ? "primary" : "secondary"}
                  onClick={() => updatePlanningView("entities")}
                >
                  Auditable Entities
                </Button>
              </div>
              <p style={helperTextStyle}>
                Switch between the planning builder and the auditable-entities catalog without leaving the audit workspace.
              </p>
            </>
          ) : (
            <p style={helperTextStyle}>
              Review active audits, step progression, scope materials, recommendations, and final reporting.
            </p>
          )}
        </div>
      </div>

      <div style={panelStyle}>
        {activeTab === "audits" ? (
          auditId ? (
            <AuditExecutionDetail auditId={auditId} embedded />
          ) : (
            <AuditList embedded />
          )
        ) : (
          <>{planningView === "entities" ? <AuditUniverseList embedded /> : <AuditPlans embedded />}</>
        )}
      </div>
    </div>
  );
}
